import './styles.scss';

import { ReactElement, memo, useMemo } from 'react';
import { getClasses, getProperty } from '@/utils';

import Table from '@/common/Table';
import { Vehicle } from '@/models/gen/graphql';
import VehicleTableHeader from './VehicleTableHeader';
import VehicleTableRow from './VehicleTableRow';
import { useVehiclesState } from '@/features/Vehicles';

const VehicleTable = (): ReactElement => {
  const [{ vehicles, search, sortBy, sortDirection }] = useVehiclesState(({ state }) => [
    {
      vehicles: state.vehicles,
      search: state.table.search,
      sortBy: state.table.sorting.column,
      sortDirection: state.table.sorting.direction,
    },
  ]);

  const searchableKeys = ['licensePlate', 'vin', 'trackingId', 'phoneNumber'];

  const filteredVehicles = useMemo((): Vehicle[] => {
    if (!search) return vehicles;
    const lowerSearch = search.toLowerCase();
    return vehicles.filter((vehicle: Vehicle): boolean =>
      searchableKeys.some((key) => {
        const value = getProperty(key, vehicle);
        return typeof value === 'string' && value.toLowerCase().includes(lowerSearch);
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicles, search]);

  const sortedVehicles = useMemo((): Vehicle[] => {
    return [...filteredVehicles].sort((a: Vehicle, b: Vehicle): number => {
      if (!sortBy || !sortDirection) return 0;
      let aValue = (getProperty(sortBy, a) as string) ?? '';
      let bValue = (getProperty(sortBy, b) as string) ?? '';
      if (sortBy === 'airportCodes') {
        aValue = Array.isArray(aValue)
          ? aValue
              .map((ac) => ac.airportCode)
              .sort((a, b) => a.localeCompare(b))
              .join(', ')
          : aValue;
        bValue = Array.isArray(bValue)
          ? bValue
              .map((ac) => ac.airportCode)
              .sort((a, b) => a.localeCompare(b))
              .join(', ')
          : bValue;
      }
      const aLower = typeof aValue === 'string' ? aValue.toLowerCase() : aValue;
      const bLower = typeof bValue === 'string' ? bValue.toLowerCase() : bValue;
      if (aLower < bLower) return sortDirection === 'asc' ? -1 : 1;
      if (aLower > bLower) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredVehicles, sortBy, sortDirection]);

  return (
    <Table className={getClasses('VehicleTable')} header={<VehicleTableHeader />} showControls>
      {sortedVehicles.map(
        (vehicle: Vehicle): ReactElement => (
          <VehicleTableRow key={vehicle.id} {...vehicle} />
        )
      )}
    </Table>
  );
};

export default memo(VehicleTable);
